import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { StubLabel } from 'Components/Texts';
import { encodeSearchParams } from 'Utils/Path';
import { updateUserProfile } from 'State/User';
import { GIFTING_QUERY_PARAM, VA_QUERY_PARAM } from 'Utils/Constants';
import { useStore } from 'Hooks/useStore';

export const EmailField = ({
  name,
  value = '',
  onBlur,
  handleChange,
  label,
  ariaLabel,
  autofocus,
  isDisabled,
  error,
  touched,
  className = '',
  formName,
}) => {
  const location = useLocation();
  const routes = useStore('appConfig.routes');
  const isGifting = useStore('purchaseMode.isGifting');
  const existingUserError = error && error === 'existing-user';
  const editEmailFields = ['newEmailAddress', 'confirmNewEmailAddress'];
  const isNewEmail = editEmailFields.find((field) => field === name);
  const user = useStore('user');
  let errorMessage = error;

  const urlParams = new URLSearchParams(location.search);
  const vaMemberId = urlParams.get(VA_QUERY_PARAM);

  let inputClassName = `formFieldInput ${touched && error ? 'inputError' : ''}`;
  inputClassName = `${inputClassName} ${value || isDisabled ? 'hasValue' : ''}`;

  const labelId = label && !ariaLabel ? `emailInput-${label.replace(/\s+/g, '')}` : null;

  let searchParam;
  if (isGifting) {
    searchParam = `${GIFTING_QUERY_PARAM}`;
  } else if (vaMemberId) {
    searchParam = `${VA_QUERY_PARAM}=${vaMemberId}`;
  } else {
    searchParam = encodeSearchParams({ return_to: `${location.pathname}${location.search}` }); //create account screen including params
  }

  useEffect(() => {
    // This prefills email fields for sign in/sign up/forget pw except for when user is editing profile email
    if ((!isNewEmail && value && existingUserError) || (!isNewEmail && touched && !error)) {
      const payload = { ...user, email: value };
      updateUserProfile(payload);
    }
    // disable lint as we only want to call updateUserProfile once when there is existing user error
    // or after user input valid email in signin form
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingUserError, touched, error, isNewEmail]);

  if (existingUserError) {
    errorMessage = (
      <span>
        {'This email is already associated with a FOX account. If you have a password '}
        <Link
          className="formFieldErrorInline"
          to={{
            pathname: routes.signin,
            search: searchParam,
            state: { from: location, page_source: 'other' },
          }}
        >
          sign in
        </Link>
        {' or use '}
        <Link
          className="formFieldErrorInline"
          to={{
            pathname: routes.resetByEmail,
            state: { from: location },
          }}
        >
          password assistance
        </Link>
        {' to create or reset your password.'}
      </span>
    );
  }

  if (existingUserError && isNewEmail) {
    errorMessage = <span>An account with this email already exist, please use a different email.</span>;
  }

  const showError = (touched && error) || (existingUserError && formName === 'createAccountForm');

  return (
    <div className={`formField formFieldEmail ${className}`}>
      <div className="animatedFieldContainer">
        <input
          className={inputClassName}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          type="email"
          disabled={isDisabled}
          autoComplete="email"
          autoFocus={autofocus}
          aria-label={ariaLabel}
          aria-labelledby={labelId}
        />
        <StubLabel text={label} id={labelId} className="animatedLabel" />
        {showError && <div className="formFieldError">{errorMessage}</div>}
      </div>
    </div>
  );
};
